// Relative imports, not the '@' alias: jest has no moduleNameMapper for '@'
// and jest.config.js may not be touched. See useCourseList.ts.
import ChessRestClient from '../../Api/ChessRestClient';
import {getLocalStorage} from '../../Utils/CommonUtils';
import {useMutation, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';

import type {GateCheck, ReviewFlag, ReviewPage} from '../../Screens/LMS/reviewLogic';

async function authHeader() {
  const token = await getLocalStorage('access_token');
  return {Authorization: `Bearer ${token}`};
}

export type ReviewChapter = {id: number; number: number; title: string; pages: ReviewPage[]};

export type Discarded = {
  time: number | null;
  fen: string | null;
  reasons: string[];
  part?: number;
  /** The funnel stage that dropped it: triage, grouping, unknown_side, … */
  stage?: string;
  source?: string;
};

export type ReviewPayload = {
  book: {
    id: number;
    uuid: string;
    title: string;
    is_public: boolean;
    /** admin, reviewer, ai, source or template. */
    title_source?: string | null;
    description?: string | null;
    thumbnail?: string | null;
  };
  review_state: 'draft' | 'in_review' | 'published';
  published_at: string | null;
  chapters: ReviewChapter[];
  flags: ReviewFlag[];
  discarded: Discarded[];
  gate: {ok: boolean; checks: GateCheck[]};
  summary: {pages: number; puzzles: number; hard: number; open_flags: number; discarded: number};
};

const key = (uuid: string) => ['course-review', uuid];

export async function fetchReview(uuid: string, opened: number[]): Promise<ReviewPayload> {
  const response = await ChessRestClient.get(`/video_learning/course/${uuid}/review/`, {
    headers: await authHeader(),
    params: opened.length ? {opened: opened.join(',')} : undefined,
  });
  return response.data as ReviewPayload;
}

export function useCourseReview(uuid: string | null, opened: number[]): UseQueryResult<ReviewPayload, Error> {
  return useQuery<ReviewPayload, Error>(
    [...key(uuid ?? ''), opened.join(',')],
    () => fetchReview(uuid as string, opened),
    {enabled: !!uuid, keepPreviousData: true},
  );
}

/** One mutation per action. Each invalidates the review payload on success. */
export function useReviewActions(uuid: string) {
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries(key(uuid));
  const h = authHeader;

  const flag = useMutation(
    async (body: {pageId: number; kind: string; note: string}) => {
      const r = await ChessRestClient.post(
        `/video_learning/course/${uuid}/page/${body.pageId}/flag/`,
        {kind: body.kind, note: body.note},
        {headers: await h()},
      );
      return r.data as ReviewFlag;
    },
    {onSuccess: refresh},
  );

  const dismiss = useMutation(
    async (body: {flagId: number; reason: string}) => {
      const r = await ChessRestClient.post(
        `/video_learning/course/${uuid}/flag/${body.flagId}/dismiss/`,
        {reason: body.reason},
        {headers: await h()},
      );
      return r.data as ReviewFlag;
    },
    {onSuccess: refresh},
  );

  const fix = useMutation(
    async (body: {pageId: number; kind: string; [k: string]: unknown}) => {
      const {pageId, ...rest} = body;
      const r = await ChessRestClient.post(
        `/video_learning/course/${uuid}/page/${pageId}/fix/`,
        rest,
        {headers: await h()},
      );
      return r.data as {page: ReviewPage; review_state: string};
    },
    {onSuccess: refresh},
  );

  const publish = useMutation(
    async (body: {opened: number[]}) => {
      const r = await ChessRestClient.post(
        `/video_learning/course/${uuid}/publish/`,
        body,
        {headers: await h()},
      );
      return r.data as {review_state: string; published_at: string};
    },
    {onSuccess: refresh},
  );

  const unpublish = useMutation(
    async () => {
      const r = await ChessRestClient.post(
        `/video_learning/course/${uuid}/unpublish/`,
        {},
        {headers: await h()},
      );
      return r.data as {review_state: string};
    },
    {onSuccess: refresh},
  );

  /** Rename or describe the whole course. The rename is the reviewer's and
   * survives regeneration. */
  const editCourse = useMutation(
    async (body: {title?: string; description?: string}) => {
      const r = await ChessRestClient.patch(`/video_learning/course/${uuid}/`, body, {
        headers: await h(),
      });
      return r.data as {title: string; description: string | null; title_source: string};
    },
    {onSuccess: refresh},
  );

  /** Queue new AI artwork; the old image stays until the new one is stored. */
  const regenerateThumbnail = useMutation(
    async () => {
      const r = await ChessRestClient.post(`/video_learning/course/${uuid}/thumbnail/`, {}, {
        headers: await h(),
      });
      return r.data as {status: string; thumbnail: string | null};
    },
    {onSuccess: refresh},
  );

  return {flag, dismiss, fix, publish, unpublish, editCourse, regenerateThumbnail};
}

/** The server's message for a refused action, or a plain sentence. */
export function errorText(error: unknown, fallback: string): string {
  return (error as any)?.response?.data?.error ?? fallback;
}
